import React, { Component } from "react";
import {
  withModulesManager,
  withHistory,
  formatMessage,
} from "@openimis/fe-core";
import { injectIntl } from "react-intl";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import {
  Typography,
  Paper,
  Box,
  Grid,
  IconButton,
  Card,
  CardContent,
  CircularProgress,
  Link,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { ArrowBack as BackIcon, Edit as EditIcon } from "@material-ui/icons";
import { withStyles } from "@material-ui/core/styles";
import {
  fetchInsureeAccountClaimEnrollment,
  submitInsureeAccountClaimEnrollment,
  reviewInsureeAccountClaimEnrollment,
  approveInsureeAccountClaimEnrollment,
  rejectInsureeAccountClaimEnrollment,
  turnToDraftInsureeAccountClaimEnrollment,
  deleteInsureeAccountClaimEnrollment,
} from "../../actions";
import EnrollmentStatusBadge from "../../components/common/EnrollmentStatusBadge";
import EnrollmentActionButtons from "../../components/common/EnrollmentActionButtons";
import KeyValueSection from "../../components/common/KeyValueSection";
import MediaPreview from "../../components/common/MediaPreview";
import {
  claimFieldRows,
  enrollmentMetaRows,
  openCreatedEnrollmentEntity,
  formatDisplayValue,
  buildFieldRows,
  targetInsureeFieldRows,
  relatedInsureeDisplayName,
} from "../../utils/enrollmentData";

const styles = (theme) => ({
  page: { margin: theme.spacing(2) },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(2),
    gap: theme.spacing(2),
  },
  titleWrap: { display: "flex", alignItems: "center", gap: theme.spacing(2), flex: 1 },
  title: { fontWeight: 700, color: theme.palette.primary.main },
  card: { marginBottom: theme.spacing(2) },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
    color: theme.palette.primary.main,
  },
  targetRow: {
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,0.06)"
        : "rgba(0,0,0,0.04)",
  },
});

class InsureeAccountClaimDetail extends Component {
  componentDidMount() {
    this.load();
  }

  componentDidUpdate(prevProps) {
    const prevId = prevProps.match?.params?.enrollmentId;
    const nextId = this.props.match?.params?.enrollmentId;
    if (prevId !== nextId) this.load();
  }

  load = () => {
    const uuid = this.props.match?.params?.enrollmentId;
    if (uuid) {
      this.props.fetchInsureeAccountClaimEnrollment(this.props.modulesManager, uuid);
    }
  };

  handleBack = () => {
    const { history } = this.props;
    if (history) history.push("/enrollment/account-claim");
    else window.location.href = "/enrollment/account-claim";
  };

  handleEdit = () => {
    const uuid = this.props.enrollment?.uuid;
    if (!uuid) return;
    const { history } = this.props;
    if (history) history.push(`/enrollment/account-claim/${uuid}/edit`);
    else window.location.href = `/enrollment/account-claim/${uuid}/edit`;
  };

  handleOpenCreated = () => {
    const { modulesManager, history, enrollment } = this.props;
    openCreatedEnrollmentEntity(modulesManager, history, "accountClaim", enrollment);
  };

  openInsuree = (uuid) => {
    if (!uuid) return;
    const { modulesManager, history } = this.props;
    const ref = modulesManager?.getRef?.("insuree.route.insuree");
    const path = ref
      ? `/${ref}/${uuid}`
      : `/insuree/insurees/insuree/${uuid}`;
    if (history) history.push(path);
    else window.location.href = path;
  };

  run = (action, payload) => {
    const mm = this.props.modulesManager;
    return Promise.resolve(action(mm, payload)).then(() => this.load());
  };

  renderRelatedInsureeTable = (insurees, emptyLabel) => {
    const { classes } = this.props;
    const rows = insurees || [];
    return (
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>CHF ID</TableCell>
            <TableCell>Name</TableCell>
            <TableCell>DOB</TableCell>
            <TableCell>Gender</TableCell>
            <TableCell>Role</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={6}>{emptyLabel}</TableCell>
            </TableRow>
          )}
          {rows.map((m) => (
            <TableRow
              key={m.uuid || m.id || m.chfId}
              hover={Boolean(m.uuid)}
              className={m.isTarget ? classes.targetRow : undefined}
              style={m.uuid ? { cursor: "pointer" } : undefined}
              onClick={() => m.uuid && this.openInsuree(m.uuid)}
            >
              <TableCell>{formatDisplayValue(m.chfId)}</TableCell>
              <TableCell>{relatedInsureeDisplayName(m)}</TableCell>
              <TableCell>{formatDisplayValue(m.dob)}</TableCell>
              <TableCell>{formatDisplayValue(m.gender)}</TableCell>
              <TableCell>
                {m.head
                  ? "Head"
                  : formatDisplayValue(m.relationship) !== "—"
                    ? formatDisplayValue(m.relationship)
                    : "—"}
              </TableCell>
              <TableCell>
                {m.isTarget && (
                  <Chip size="small" color="primary" label="Claim target" />
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  };

  render() {
    const { classes, intl, enrollment, loading } = this.props;
    if (loading && !enrollment) {
      return (
        <Box className={classes.page} display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      );
    }
    if (!enrollment) {
      return (
        <Box className={classes.page}>
          <Typography>Enrollment not found.</Typography>
          <IconButton onClick={this.handleBack}>
            <BackIcon />
          </IconButton>
        </Box>
      );
    }

    const claimRows = claimFieldRows(enrollment.claimData);
    const metaRows = enrollmentMetaRows(enrollment);
    const target = enrollment.targetInsuree;
    const targetRows = targetInsureeFieldRows(target);
    const familyMembers = enrollment.familyMembers || [];
    const policyHolderContexts = enrollment.policyHolderContexts || [];
    let linkedConflict = enrollment.linkedAccountConflict;
    if (typeof linkedConflict === "string") {
      try {
        linkedConflict = JSON.parse(linkedConflict);
      } catch (e) {
        linkedConflict = null;
      }
    }
    const conflictLabel = linkedConflict
      ? [
          linkedConflict.username,
          linkedConflict.email,
          linkedConflict.phone,
        ]
          .filter(Boolean)
          .join(" / ") || linkedConflict.id
      : null;
    const docRows = buildFieldRows([
      ["Insuree user ID", enrollment.insureeUserId],
      ["Identity document type", enrollment.identityDocumentType],
    ]);

    return (
      <Box className={classes.page}>
        <Box className={classes.header}>
          <Box className={classes.titleWrap}>
            <IconButton onClick={this.handleBack}>
              <BackIcon />
            </IconButton>
            <Typography variant="h5" className={classes.title}>
              {formatMessage(intl, "enrollment", "accountClaimEnrollment.detail") ||
                "Account Claim Enrollment"}
            </Typography>
            <EnrollmentStatusBadge status={enrollment.status} />
          </Box>
          {enrollment.status === "DRAFT" && (
            <IconButton color="primary" onClick={this.handleEdit}>
              <EditIcon />
            </IconButton>
          )}
        </Box>

        {conflictLabel && enrollment.status !== "APPROVED" && (
          <Alert severity="error" style={{ marginBottom: 16 }}>
            Target insuree is already linked to another account (
            {conflictLabel}). Unlink that account before approving this claim.
          </Alert>
        )}

        <Card className={classes.card}>
          <CardContent>
            <EnrollmentActionButtons
              intl={intl}
              enrollment={enrollment}
              onEdit={this.handleEdit}
              onOpenCreated={
                enrollment.targetInsuree?.uuid ? this.handleOpenCreated : undefined
              }
              openCreatedLabel="Open linked insuree"
              onSubmit={(p) =>
                this.run(this.props.submitInsureeAccountClaimEnrollment, p)
              }
              onReview={(p) =>
                this.run(this.props.reviewInsureeAccountClaimEnrollment, p)
              }
              onApprove={(p) =>
                this.run(this.props.approveInsureeAccountClaimEnrollment, p)
              }
              onReject={(p) =>
                this.run(this.props.rejectInsureeAccountClaimEnrollment, p)
              }
              onTurnToDraft={(p) =>
                this.run(this.props.turnToDraftInsureeAccountClaimEnrollment, p)
              }
              onDelete={(uuid) =>
                this.run(this.props.deleteInsureeAccountClaimEnrollment, uuid).then(
                  this.handleBack
                )
              }
            />
          </CardContent>
        </Card>

        <Grid container spacing={2}>
          <Grid item xs={12} md={7}>
            <Paper className={classes.card}>
              <Box p={2}>
                <KeyValueSection title="Submitted claim" rows={claimRows} />
              </Box>
            </Paper>

            <Paper className={classes.card}>
              <Box p={2}>
                <KeyValueSection
                  title="Target insuree (to link)"
                  rows={targetRows}
                  emptyMessage="No target insuree resolved for this claim."
                />
                {target?.uuid && (
                  <Box mt={1}>
                    <Link
                      component="button"
                      variant="body2"
                      onClick={() => this.openInsuree(target.uuid)}
                    >
                      Open insuree record
                    </Link>
                  </Box>
                )}
              </Box>
            </Paper>

            {familyMembers.length > 0 && (
              <Paper className={classes.card}>
                <Box p={2}>
                  <Typography variant="h6" className={classes.sectionTitle}>
                    Family members
                  </Typography>
                  {this.renderRelatedInsureeTable(
                    familyMembers,
                    "No family members"
                  )}
                </Box>
              </Paper>
            )}

            {policyHolderContexts.map((ph) => (
              <Paper className={classes.card} key={ph.id || ph.code}>
                <Box p={2}>
                  <Typography variant="h6" className={classes.sectionTitle}>
                    Policy holder: {formatDisplayValue(ph.tradeName)}
                    {ph.code ? ` (${ph.code})` : ""}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Other insurees linked to this policy holder
                  </Typography>
                  {this.renderRelatedInsureeTable(
                    ph.insurees,
                    "No linked insurees"
                  )}
                </Box>
              </Paper>
            ))}

            <Paper className={classes.card}>
              <Box p={2}>
                <KeyValueSection title="Identity documents" rows={docRows} />
                <MediaPreview
                  label="Identity document"
                  url={enrollment.identityDocumentUrl}
                />
                <MediaPreview
                  label="Insuree card"
                  url={enrollment.insureeCardUrl}
                />
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} md={5}>
            <Paper className={classes.card}>
              <Box p={2}>
                <KeyValueSection title="Enrollment info" rows={metaRows} />
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    );
  }
}

const mapStateToProps = (state) => ({
  enrollment: state.enrollment?.accountClaimEnrollmentDetail,
  loading: state.enrollment?.fetchingAccountClaimEnrollments,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      fetchInsureeAccountClaimEnrollment,
      submitInsureeAccountClaimEnrollment,
      reviewInsureeAccountClaimEnrollment,
      approveInsureeAccountClaimEnrollment,
      rejectInsureeAccountClaimEnrollment,
      turnToDraftInsureeAccountClaimEnrollment,
      deleteInsureeAccountClaimEnrollment,
    },
    dispatch
  );

export default withModulesManager(
  withHistory(
    injectIntl(
      withStyles(styles)(
        connect(mapStateToProps, mapDispatchToProps)(InsureeAccountClaimDetail)
      )
    )
  )
);
